import { useState, useRef } from 'react';
import { toast } from '@/hooks/use-toast';
import { ConversationMessage, ResponseLength, ScenarioType, TurnOrder } from '../../types';
import { 
  validateConversationRequirements,
  handleInitialRound,
  handleAdditionalRounds,
  checkBeforeStarting
} from './agent/conversationManager';

export const useAgentConversation = (
  apiKey: string | null,
  agentAModel: string,
  agentBModel: string,
  agentCModel: string,
  agentAPersona: string,
  agentBPersona: string,
  agentCPersona: string,
  numberOfAgents: number,
  rounds: number,
  responseLength: ResponseLength,
  getCurrentScenario: () => ScenarioType,
  getCurrentPrompt: () => string,
  turnOrder: TurnOrder = 'sequential'
) => {
  const [conversation, setConversation] = useState<ConversationMessage[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const isStartingRef = useRef(false); // Guard against concurrent starts

  const handleStartConversation = async (): Promise<ConversationMessage[] | null> => {
    // Prevent concurrent conversation starts
    if (isStartingRef.current) {
      console.log('Conversation already starting, ignoring request');
      return null;
    }
    isStartingRef.current = true;
    const currentPrompt = getCurrentPrompt();
    const currentScenario = getCurrentScenario();
    
    // First check API availability
    setIsLoading(true);
    
    // Always use shared key mode (edge function with server-side API key)
    const effectiveApiKey: string | null = null;
    
    console.log("Starting conversation with shared key mode");
    console.log("Agent models:", { agentAModel, agentBModel, agentCModel });
    console.log("Number of agents:", numberOfAgents);
    console.log("Rounds:", rounds);
    
    // Check if the API is available before proceeding
    const apiAvailable = await checkBeforeStarting(effectiveApiKey);
    if (!apiAvailable) {
      setIsLoading(false);
      isStartingRef.current = false;
      return null;
    }
    
    // Validate requirements before starting
    if (!validateConversationRequirements(
      currentPrompt,
      effectiveApiKey,
      agentAModel,
      agentBModel,
      agentCModel,
      numberOfAgents
    )) {
      setIsLoading(false);
      isStartingRef.current = false;
      return null;
    }
    
    try {
      // Clear any previous conversation
      setConversation([]);
      
      // Start with initial round
      const { 
        conversationMessages, 
        agentAResponse, 
        agentBResponse 
      } = await handleInitialRound(
        currentPrompt,
        currentScenario,
        numberOfAgents,
        agentAModel,
        agentBModel,
        agentCModel,
        agentAPersona,
        agentBPersona,
        agentCPersona,
        effectiveApiKey,
        responseLength,
        undefined,
        undefined,
        turnOrder
      );
      
      // Update conversation with initial messages
      setConversation(conversationMessages);
      
      let finalMessages = conversationMessages;
      
      // If more than one round, handle additional rounds
      if (rounds > 1) {
        const additionalMessages = await handleAdditionalRounds(
          currentPrompt,
          currentScenario,
          rounds,
          numberOfAgents,
          agentAModel,
          agentBModel,
          agentCModel,
          agentAPersona,
          agentBPersona,
          agentCPersona,
          agentAResponse,
          agentBResponse,
          conversationMessages,
          effectiveApiKey,
          responseLength,
          undefined,
          undefined,
          turnOrder
        );
        
        // Update conversation with all messages
        setConversation(additionalMessages);
        finalMessages = additionalMessages;
      }
      
      toast({
        title: "Conversation Complete",
        description: `Agents have completed their ${rounds} round${rounds > 1 ? 's' : ''} of conversation.`,
      });
      
      return finalMessages;
    } catch (error) {
      console.error("Error in conversation flow:", error);
      
      toast({
        title: "Conversation Error",
        description: error instanceof Error ? error.message : "An error occurred during the conversation.",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsLoading(false);
      isStartingRef.current = false;
    }
  };

  return {
    conversation,
    setConversation,
    isLoading,
    setIsLoading,
    handleStartConversation
  };
};
